import { useQuery } from '@tanstack/react-query'
import { Button, Checkbox, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { ModalErrorState, ModalLoadingState } from '../../../components/ModalStateHandlers'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { trpc } from '../../../utils/trpc'
import { ComponentRenderer } from '../cycle-execution/ComponentRenderer'
import { PdfDownloadButton } from '../cycle-execution/PdfDownloadButton'
import type { FormComponentWithReports } from '../cycle-execution/reports/types'

interface EvaluationHeaderField {
	id?: string
	name: string
	value: string | number
	order: number
	sources?: Array<{ type: 'customFields' | 'hierarchy' | 'user' | 'system' | 'evaluator'; key: string }>
	formId?: string | null
}

export const ReviewPreviewPage = () => {
	const { evaluationId, cycleId } = useParams<{ evaluationId: string; cycleId: string }>()
	const navigate = useNavigate()
	const methods = useForm()
	const [isPreviewMode, setIsPreviewMode] = useState(false)

	const { data, isLoading, isError, error, refetch } = useQuery({
		...trpc.client.tasks.getReviewPreview.queryOptions({
			evaluationId: evaluationId || '',
			previewAsRole: isPreviewMode ? ('participant' as const) : undefined,
		}),
		enabled: !!evaluationId,
	})

	// Set document title for default PDF filename: CycleName-ParticipantName-Timestamp
	useEffect(() => {
		if (data?.evaluation && data?.participant) {
			const cycleName = data.evaluation.cycleName || 'Cycle'
			const participantName =
				`${data.participant.firstName || ''} ${data.participant.lastName || ''}`.trim() || 'Self Evaluator'
			const timestamp = new Date().toISOString().split('T')[0] // YYYY-MM-DD format

			// Clean up names to be filename-safe (remove special characters)
			const cleanCycleName = cycleName.replace(/[^a-zA-Z0-9-_\s]/g, '').replace(/\s+/g, '-')
			const cleanParticipantName = participantName.replace(/[^a-zA-Z0-9-_\s]/g, '').replace(/\s+/g, '-')

			const filename = `${cleanCycleName}-${cleanParticipantName}-${timestamp}`
			document.title = filename
		}

		// Cleanup: restore original title on unmount
		return () => {
			document.title = 'Review Preview'
		}
	}, [data])

	if (isLoading) {
		return <ModalLoadingState title='Loading Review Preview...' onClose={() => navigate(-1)} />
	}

	if (isError || !data) {
		return (
			<ModalErrorState
				errorMessage='Unable to load preview'
				errorDescription='There was a problem loading the review preview. Please try again or go back.'
				technicalDetails={error?.message}
				logoUrl={sessionStore.getState().organization?.logo ?? undefined}
				onClose={() => navigate(-1)}
				onRetry={() => refetch()}
			/>
		)
	}

	const formatFieldValue = (field: EvaluationHeaderField) => {
		const rawValue = field.value
		if (rawValue === undefined || rawValue === null || rawValue === '') return '-'

		const isSystemUpdatedAt = field.sources?.some((source) => source.type === 'system' && source.key === 'updatedAt')

		if (isSystemUpdatedAt) {
			const date = new Date(String(rawValue))
			if (!Number.isNaN(date.getTime())) {
				return date.toLocaleString(undefined, {
					year: 'numeric',
					month: 'short',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit',
				})
			}
		}

		return String(rawValue)
	}

	return (
		<FormProvider {...methods}>
			<div className='w-full h-full flex flex-col'>
				{/* Header */}
				<div className='border-b border-neutral-200 p-4'>
					<div className='max-w-7xl mx-auto flex items-center justify-between'>
						<Text variant='large' weight='semibold'>
							Review Preview
						</Text>
						<div className='flex gap-2'>
							<PdfDownloadButton variant='secondary' size='small' />
							<Button variant='secondary' size='small' onClick={() => navigate(`/evaluations/${cycleId}`)}>
								Back to Search
							</Button>
						</div>
					</div>
				</div>

				{/* Main Content */}
				<div className='flex-1 overflow-y-auto p-6'>
					<div className='max-w-7xl mx-auto'>
						{/* Participant Info */}
						{data.participant && (
							<div className='mb-6 p-4 rounded-md bg-blue-50 border border-blue-200'>
								<div className='grid grid-cols-2 gap-4'>
									<div className='min-w-0'>
										<Text variant='hint' className='text-neutral-400 mb-1'>
											Name
										</Text>
										<Text variant='xsmall' className='break-words'>
											{String(data.participant.firstName)} {String(data.participant.lastName)}
										</Text>
									</div>
									{data.evaluationHeaderConfig?.map((field: EvaluationHeaderField, index) => (
										<div key={field.id ?? `field-${index}`} className='min-w-0'>
											<Text variant='hint' className='text-neutral-400 mb-1'>
												{field.name}
											</Text>
											<Text variant='xsmall' className='break-words'>
												{formatFieldValue(field)}
											</Text>
										</div>
									))}
								</div>
							</div>
						)}

						{/* Preview Toggle */}
						<div className='mb-6 p-4 rounded-md bg-neutral-50 border border-neutral-200 print:hidden'>
							<span className='flex items-start gap-2 cursor-pointer'>
								<Checkbox checked={isPreviewMode} onChange={(e) => setIsPreviewMode(e.target.checked)} />
								<div>
									<Text variant='xsmall' weight='semibold'>
										Preview as Participant
									</Text>
									<Text variant='xsmall' className='text-neutral-500'>
										View how participants will see this review (anonymized view)
									</Text>
								</div>
							</span>
						</div>

						{/* Form Components */}
						<div className='space-y-4'>
							{data.formComponents?.map((component: FormComponentWithReports) => (
								<div key={component.id}>
									<ComponentRenderer
										component={component}
										control={methods.control}
										evaluationId={evaluationId || ''}
										currentStepId=''
										userRole='cycle_admin'
										canEdit={false}
										allowCAEdit={data.allowCAEdit || false}
										allFormComponents={data.formComponents}
									/>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</FormProvider>
	)
}
